import React, { useState, useEffect, useContext } from 'react';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import GeneralInfo from '../../components/dashboard/GeneralInfo';
import Recommendations from '../../components/dashboard/Recommendations';
import DayPlans from '../../components/dashboard/DayPlans';
import ShoppingList from '../../components/dashboard/ShoppingList';
import { AuthContext } from '../../context/AuthContext';
import { Helmet } from 'react-helmet-async';
import api from '../../services/api';
import nutritionService from '../../services/nutritionService';

const UserPlanView = () => {
  const { user } = useContext(AuthContext);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shoppingList, setShoppingList] = useState([]);

  useEffect(() => {
    const fetchUserPlan = async () => {
      try {
        // V2.0: Utiliser l'API client pour récupérer le plan actif
        const data = await api.get('/client/weekplan/current');

        if (!data || !data.dayPlans) {
          setError('Aucun plan actif');
          setLoading(false);
          return;
        }

        // Normaliser client -> user pour compatibilité
        if (data.client && !data.user) {
          data.user = data.client;
        }

        setPlan(data);

        // V2.0: Récupérer la liste de courses via l'API si le plan a un ID
        if (data._id) {
          try {
            const shoppingData = await nutritionService.getShoppingListFromPlan(data._id);
            if (shoppingData?.items) {
              setShoppingList(shoppingData.items);
            }
          } catch (shoppingErr) {
            console.warn('Shopping list API error, generating locally:', shoppingErr.message);
            // Fallback: générer localement depuis les ingrédients du plan
            const allIngredients = [];
            for (const dayPlan of data.dayPlans || []) {
              for (const mealKey of ['breakfast', 'morningSnack', 'lunch', 'snack', 'dinner']) {
                const meal = dayPlan.meals?.[mealKey];
                if (meal?.ingredients) {
                  allIngredients.push(...meal.ingredients);
                }
              }
            }

            // Agréger les ingrédients
            const aggregated = {};
            for (const ing of allIngredients) {
              const key = ing.name || ing.ciqual_code;
              if (aggregated[key]) {
                aggregated[key].quantity += ing.quantity || 0;
              } else {
                aggregated[key] = { ...ing };
              }
            }

            setShoppingList(Object.values(aggregated).map(ing => ({
              ingredient: ing.name,
              quantity: Math.round(ing.quantity || 0),
              unit: ing.unit || 'g'
            })));
          }
        }

        setLoading(false);
      } catch (err) {
        console.error('Erreur lors de la récupération des données :', err.message);
        setError(err.message);
        setLoading(false);
      }
    };

    if (user) {
      fetchUserPlan();
    }
  }, [user]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !plan) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '75vh',
          textAlign: 'center',
        }}
      >
        <Typography variant="h2" sx={{ mb: 2 }}>
        Bonjour {user?.firstName || ''}
        </Typography>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          Vous n'avez pas de programme actif.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            href="/contact"
            sx={{ padding: '10px 20px'}}
          >
            J'en veux un
          </Button>
          <Button
            variant="text"
            color="black"
            href="/contact"
          >
            Obtenir de l'aide
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>Yoco - Dashboard</title>
        <meta name="keywords" content="coaching, sport, nutrition, bien-être, performance" />
      </Helmet>
      <Box
        sx={{
          px: { xs: 2, sm: 6 },
          pt: 3,
          pb: 6,
          maxWidth: '880px',
          width: { xs: '-webkit-fill-available', md: '100%' },
          margin: '0 auto',
        }}
      >
        <GeneralInfo
          user={plan.user}
          dietaryPreferences={plan.user?.dietaryPreferences}
          goals={plan.user?.goals}
          allergies={plan.user?.allergies}
          summary={plan.summary}
          targetMacros={plan.user?.targetMacros}
          planId={plan._id}
        />
        <Recommendations recommendations={plan.recommendations} />
        <DayPlans dayPlans={plan.dayPlans} targetMacros={plan.user?.targetMacros} />
        <ShoppingList shoppingList={shoppingList} />
      </Box>
    </>
  );
};

export default UserPlanView;
